/**
 * PageSnapshotSheet — shows what Peersheep has stored for a watched page:
 * the latest structured reading (SEO/meta tags, headings, prices, navigation,
 * CTAs, structured data, detected tools). Every field here is diffed on each
 * crawl, so this is exactly what a change alert can be triggered by.
 */
import { useEffect, useState } from "react";
import { watchedUrls as watchedUrlsApi } from "@/lib/api";
import type { WatchedUrl, WatchedUrlSnapshot } from "@/types";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Skeleton } from "@/components/ui/skeleton";
import { formatDateTime, pageTypeLabel } from "@/lib/utils";

export function PageSnapshotSheet({
  url,
  onClose,
}: {
  url: WatchedUrl | null;
  onClose: () => void;
}) {
  const [data, setData] = useState<WatchedUrlSnapshot | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!url) return;
    let cancelled = false;
    setIsLoading(true);
    setData(null);
    watchedUrlsApi.snapshot(url.id).then((res) => {
      if (cancelled) return;
      if ("data" in res && res.data) setData(res.data);
      setIsLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [url]);

  const s = data?.snapshot;

  return (
    <Sheet open={!!url} onOpenChange={(open) => !open && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-lg p-0 flex flex-col">
        <SheetHeader className="border-b border-border px-5 py-4 space-y-1">
          <SheetTitle className="font-mono text-[11px] font-semibold uppercase tracking-widest">
            Stored page snapshot{url ? ` · ${pageTypeLabel(url.label)}` : ""}
          </SheetTitle>
          <SheetDescription className="font-mono text-[11px] break-all">{url?.url}</SheetDescription>
          {data?.capturedAt && (
            <p className="font-mono text-[11px] text-muted-foreground">
              v{data.versionNumber} · captured {formatDateTime(data.capturedAt)} · {data.versionsStored} version
              {data.versionsStored === 1 ? "" : "s"} stored
            </p>
          )}
        </SheetHeader>

        <ScrollArea className="flex-1">
          <div className="px-5 py-4 space-y-5">
            {isLoading && (
              <div className="space-y-3">
                {[...Array(6)].map((_, i) => (
                  <Skeleton key={i} className="h-10 w-full" />
                ))}
              </div>
            )}

            {!isLoading && !s && (
              <p className="font-mono text-[11px] text-muted-foreground leading-relaxed">
                No structured snapshot yet. It's captured on the next crawl — use “Crawl now” to capture it immediately.
              </p>
            )}

            {s && (
              <>
                <Section title="SEO & meta tags">
                  <Field label="Title" value={s.seo.title} />
                  <Field label="Description" value={s.seo.metaDescription} />
                  <Field label="Canonical" value={s.seo.canonical} />
                  <Field label="Robots" value={s.seo.robots} />
                  <Field label="Language" value={s.seo.lang} />
                  <Field label="Final URL" value={s.finalUrl} />
                  {s.seo.hreflang.length > 0 && <List label="Hreflang" items={s.seo.hreflang} />}
                </Section>

                <Section title="Social cards">
                  <Field label="OG title" value={s.seo.ogTitle} />
                  <Field label="OG description" value={s.seo.ogDescription} />
                  <Field label="OG image" value={s.seo.ogImage} />
                  <Field label="Twitter title" value={s.seo.twitterTitle} />
                </Section>

                <Section title="Pricing" count={s.prices.length}>
                  <List items={s.prices} empty="No prices found on this page" />
                </Section>

                <Section title="Headings">
                  <List label="H1" items={s.headings.h1} />
                  <List label="H2" items={s.headings.h2} />
                  {s.headings.h3.length > 0 && <List label="H3" items={s.headings.h3} collapsed />}
                </Section>

                <Section title="Calls to action" count={s.ctas.length}>
                  <List items={s.ctas} empty="None detected" />
                </Section>

                <Section title="Navigation" count={s.navLinks.length}>
                  <List items={s.navLinks} empty="None detected" />
                  {s.footerLinks.length > 0 && (
                    <List label={`Footer (${s.footerLinks.length})`} items={s.footerLinks} collapsed />
                  )}
                </Section>

                <Section title="Structured data" count={s.structuredData.length}>
                  <List items={s.structuredData} empty="No JSON-LD on this page" />
                </Section>

                <Section title="Tools & technology" count={s.tech.length}>
                  <List items={s.tech} empty="None detected" />
                </Section>

                <p className="font-mono text-[11px] text-muted-foreground">
                  {s.wordCount.toLocaleString()} words of body text stored alongside this snapshot.
                </p>
              </>
            )}
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}

function Section({ title, count, children }: { title: string; count?: number; children: React.ReactNode }) {
  return (
    <div className="space-y-2">
      <p className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground pb-2 border-b border-border">
        {title}
        {count !== undefined && <span className="ml-1.5 text-muted-foreground/60">{count}</span>}
      </p>
      <div className="space-y-2">{children}</div>
    </div>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex gap-3 font-mono text-[11px] leading-relaxed">
      <span className="w-24 shrink-0 text-muted-foreground">{label}</span>
      <span className="min-w-0 flex-1 break-words text-foreground/80">
        {value || <span className="text-muted-foreground/50">—</span>}
      </span>
    </div>
  );
}

function List({
  label,
  items,
  empty,
  collapsed = false,
}: {
  label?: string;
  items: string[];
  empty?: string;
  collapsed?: boolean;
}) {
  const [open, setOpen] = useState(!collapsed);
  if (items.length === 0) {
    return empty ? <p className="font-mono text-[11px] text-muted-foreground/60">{empty}</p> : null;
  }
  return (
    <div className="font-mono text-[11px] leading-relaxed">
      {label && (
        <button
          onClick={() => setOpen((v) => !v)}
          className="text-muted-foreground hover:text-foreground transition-colors"
        >
          {collapsed ? (open ? "▾ " : "▸ ") : ""}
          {label}
        </button>
      )}
      {open && (
        <ul className="mt-1 space-y-0.5">
          {items.map((item) => (
            <li key={item} className="break-words text-foreground/80">
              {item}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
